import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import type { RootState, AppDispatch } from '../app/store'
import { appApi } from '../app/appApiQueries'
import { App } from '../app/root'
import { zoomSdkApi } from '../app/zoomSdkQueries'

/**
 * Utility to compare two device versions.
 * @param currentVersion
 * @param minimumSupportedVersion
 */
const compareVersions = (
  currentVersion: string,
  minimumSupportedVersion: string
): number => {
  const currentParts = currentVersion.split('.').map(Number)
  const minimumParts = minimumSupportedVersion.split('.').map(Number)

  const maxLength = Math.max(currentParts.length, minimumParts.length)

  for (let i = 0; i < maxLength; i++) {
    const currentPart = currentParts[i] || 0
    const minimumPart = minimumParts[i] || 0

    if (currentPart > minimumPart) return 1
    if (currentPart < minimumPart) return -1
  }

  return 0
}

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch.withTypes<AppDispatch>()()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
export const useIsSharer = (app: App) => {
  const { isSharer } = appApi.endpoints.sharerDetails.useQueryState(app, {
    selectFromResult: ({ data }) => ({
      isSharer: data?.isSharer,
    }),
  })
  return isSharer
}
export const useSelectSharerScreenName = (app: App) => {
  const { sharerScreenName } = appApi.endpoints.sharerDetails.useQueryState(
    app,
    {
      selectFromResult: ({ data }) => ({
        sharerScreenName: data?.sharerScreenName,
      }),
    }
  )
  return sharerScreenName
}
export const useSelectAuth = (app: App) => {
  const { auth } = appApi.endpoints.login.useQueryState(app, {
    selectFromResult: ({ data }) => ({
      auth: data?.data,
    }),
  })
  return auth
}
export const useSelectNeedsEmailConsent = (app: App) => {
  const { needsEmailConsent } =
    appApi.endpoints.requiresEmailConsent.useQueryState(app, {
      selectFromResult: (result) => ({
        needsEmailConsent: result?.data?.needsEmailConsent,
      }),
    })
  return needsEmailConsent
}
export const useSelectRunningContext = () => {
  const { runningContext } = zoomSdkApi.endpoints.config.useQueryState(
    'config',
    {
      selectFromResult: (result) => ({
        runningContext: result?.data?.runningContext,
      }),
    }
  )
  return runningContext
}
export const useSelectUnfurlLink = () => {
  const { unfurlLink } = zoomSdkApi.endpoints.config.useQueryState('config', {
    selectFromResult: (result) => ({
      unfurlLink: result?.data?.launchContext?.query,
    }),
  })
  return unfurlLink
}
export const useSelectScreenName = () => {
  const { screenName } = zoomSdkApi.endpoints.getUserContext.useQueryState(
    'getUserContext',
    {
      selectFromResult: (result) => ({
        screenName: result?.data?.screenName,
      }),
    }
  )
  return screenName
}
export const useSelectOrigin = () => {
  const { origin } = zoomSdkApi.endpoints.config.useQueryState('config', {
    selectFromResult: (result) => ({
      origin: result?.data?.launchContext?.origin,
    }),
  })
  return origin
}

export const useSelectMobileDeviceInfo = () => {
  const { isMobileDevice, clientVersion, userAgent } =
    zoomSdkApi.endpoints.config.useQueryState('config', {
      selectFromResult: (result) => {
        const clientVersion = result?.data?.clientVersion
        return {
          isMobileDevice: result?.data?.product === 'mobile',
          clientVersion,
          userAgent: result?.data?.userAgent,
        }
      },
    })
  // Check if a mobile client version is supported (6.5.5 and above)
  const isLatestMobileClient =
    clientVersion && compareVersions(clientVersion, '6.5.5') >= 0 // Non-Android mobile devices need version 6.5.5+

  const isAndroid = userAgent?.includes('Android') // Android devices are always supported

  return {
    isMobileDevice,
    isLatestMobileClient,
    isAndroid,
  }
}
